/**
 * Contract basis helpers (#7320, ADR-081 D5.2a).
 *
 * The API stores a policy's / benefit's contract basis as a canonical "N/M":
 * N months to incur (the window ends at the term end), M months from the policy
 * start to pay. "Paid" / "Incurred" wording is framing the system no longer keeps,
 * so the UI collects the two numbers and never free text. The API still accepts
 * carrier wording on write (document extraction needs that) and normalizes it.
 *
 * One sentinel: a pure paid-basis contract ("Paid", no incurred restriction) is
 * stored as 999/M. The UI never shows 999 — it renders and collects it as "Paid".
 */

export interface ContractBasisMonths {
  incurred: number;
  paid: number;
}

/** Upper bound per side, matching the API (benefit.NormalizeContractBasis). */
export const CONTRACT_BASIS_MAX_MONTHS = 1200;

/**
 * Incurred-months sentinel for a pure paid-basis contract: no incurred
 * restriction. Matches benefit.ContractBasisUnlimitedIncurredMonths in the API.
 */
export const CONTRACT_BASIS_PAID_INCURRED = 999;

/** Quick picks shown beside the inputs. The common stop-loss contracts. */
export const CONTRACT_BASIS_PRESETS: readonly string[] = ["12/12", "12/15", "12/18", "24/12", "36/12"];

const CANONICAL = /^([1-9][0-9]{0,3})\/([1-9][0-9]{0,3})$/;

/** Parses a canonical "N/M" string. Returns null for anything else, including "". */
export const parseContractBasis = (value: string | null | undefined): ContractBasisMonths | null => {
  if (!value) return null;
  const m = CANONICAL.exec(value.trim());
  if (!m) return null;
  const incurred = Number(m[1]);
  const paid = Number(m[2]);
  if (!isValidMonths(incurred) || !isValidMonths(paid)) return null;
  return { incurred, paid };
};

export const isValidMonths = (n: number): boolean => Number.isInteger(n) && n >= 1 && n <= CONTRACT_BASIS_MAX_MONTHS;

/** True when the parsed basis is a pure paid contract (no incurred restriction). */
export const isPaidBasis = (basis: ContractBasisMonths | null): boolean =>
  !!basis && basis.incurred === CONTRACT_BASIS_PAID_INCURRED;

/** Formats the two month counts as the stored "N/M". */
export const formatContractBasis = (incurred: number, paid: number): string => `${incurred}/${paid}`;

/** The stored value for a pure paid basis with the given paid months. */
export const formatPaidBasis = (paid: number): string => formatContractBasis(CONTRACT_BASIS_PAID_INCURRED, paid);

/**
 * Display form: "12 / 15", or "Paid / 12" for the paid-basis sentinel. Falls back
 * to the raw value for legacy wording and "-" for empty.
 */
export const displayContractBasis = (value: string | null | undefined): string => {
  const parsed = parseContractBasis(value);
  if (parsed) return `${isPaidBasis(parsed) ? "Paid" : parsed.incurred} / ${parsed.paid}`;
  return value?.trim() ? value : "-";
};

/**
 * Validates a partial pair the way the input collects it. Both blank is allowed
 * (unknown basis, stored NULL); one blank is an error; out-of-range is an error.
 */
export const validateContractBasisPair = (incurred: string, paid: string): string | undefined => {
  const i = incurred.trim();
  const p = paid.trim();
  if (i === "" && p === "") return undefined;
  if (i === "" || p === "") return "Enter both the incurred and the paid months";
  const ni = Number(i);
  const np = Number(p);
  if (!isValidMonths(ni) || !isValidMonths(np)) {
    return `Months must be whole numbers from 1 to ${CONTRACT_BASIS_MAX_MONTHS}`;
  }
  return undefined;
};

/**
 * Export view of a policy record: every contract_basis the row carries (its own and the
 * nested specific / aggregate benefit) is rendered with displayContractBasis so the
 * 999 sentinel never leaves the system as a number (#7320 AC2, !4843 thread 29877).
 */
export const displayContractBasisForExport = <T extends Record<string, unknown>>(record: T): T => {
  const out: Record<string, unknown> = { ...record };
  if ("contract_basis" in out)
    out.contract_basis = displayContractBasis(out.contract_basis as string | null | undefined);
  for (const key of ["specific_benefit", "aggregate_benefit"]) {
    const nested = out[key];
    if (nested && typeof nested === "object" && "contract_basis" in (nested as Record<string, unknown>)) {
      const n = { ...(nested as Record<string, unknown>) };
      n.contract_basis = displayContractBasis(n.contract_basis as string | null | undefined);
      out[key] = n;
    }
  }
  return out as T;
};
